'use client';

export type NotificationType = 'match' | 'like' | 'message' | 'profile_view';

interface PushNotificationPayload {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  url?: string;
}

function getNotificationPayload(type: NotificationType, name: string, extra?: string): PushNotificationPayload {
  const icon = '/assets/images/app_logo.png';
  switch (type) {
    case 'match':
      return {
        title: '💛 New Match!',
        body: `You and ${name} matched on MonerManush!`,
        icon,
        tag: `match-${name}`,
        url: '/matches',
      };
    case 'like':
      return {
        title: '❤️ Someone Liked You!',
        body: `${name} liked your profile. Check it out!`,
        icon,
        tag: `like-${name}`,
        url: '/matches',
      };
    case 'message':
      return {
        title: `💬 ${name}`,
        body: extra || `${name} sent you a message`,
        icon,
        tag: `message-${name}`,
        url: '/messaging',
      };
    case 'profile_view':
      return {
        title: '👀 Profile View',
        body: `${name} viewed your profile`,
        icon,
        tag: `view-${name}`,
        url: '/matches',
      };
  }
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return reg;
  } catch (err) {
    console.error('SW registration failed:', err);
    return null;
  }
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return await Notification.requestPermission();
}

export function getNotificationPermission(): NotificationPermission | null {
  if (typeof window === 'undefined' || !('Notification' in window)) return null;
  return Notification.permission;
}

export async function sendBrowserNotification(
  type: NotificationType,
  name: string,
  extra?: string
): Promise<void> {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  const payload = getNotificationPayload(type, name, extra);

  // Use service worker notification if available (supports click-to-open)
  if ('serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification(payload.title, {
        body: payload.body,
        icon: payload.icon,
        badge: payload.icon,
        tag: payload.tag,
        data: { url: payload.url },
        requireInteraction: false,
      } as NotificationOptions);
      return;
    } catch {
      // fallback to basic Notification
    }
  }

  // Fallback: basic Notification API
  const notif = new Notification(payload.title, {
    body: payload.body,
    icon: payload.icon,
    tag: payload.tag,
  });
  notif.onclick = () => {
    window.focus();
    if (payload.url) window.location.href = payload.url;
    notif.close();
  };
}
